import React from 'react';

import PropTypes from 'prop-types';
import {Form, FormikProps} from 'formik';
import {CallMade} from '@material-ui/icons';
import {Box, Grid} from '@material-ui/core';

import {FieldShape} from 'commons/shapes';
import {InferPropTypes} from 'commons/types/Types';
import {EfeResponse, PersonalDataServiceRequest, ValidationsCURPResponse} from 'commons/models';
import {EfeButtonPrimary, EfeFormSection, EfeSupportMessage} from 'components';

import {functions} from 'commons/utils';
import {CatalogServices, ValidationsService} from 'services';
import {HttpCodes, Messages, SnackbarVariants} from 'commons/constants';
import {AxiosError} from 'axios';
import {useSpinner} from 'hooks';
import {useSnackbar} from 'notistack';

InnerFormDocsInfo.propTypes = {
    foreign: PropTypes.bool.isRequired,
    catBanks: PropTypes.any,
    fields: PropTypes.arrayOf(PropTypes.shape(FieldShape).isRequired).isRequired,
};

function InnerFormDocsInfo(
    props: InferPropTypes<
        typeof InnerFormDocsInfo.propTypes & FormikProps<PersonalDataServiceRequest>
    >
) {
    const {enqueueSnackbar} = useSnackbar();

    const {values, errors, fields, isSubmitting, setFieldValue, foreign, catBanks} = props;

    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    function setValues(data: ValidationsCURPResponse) {
        setFieldValue('curp', data.curp);
        setFieldValue('name', data.nombre);
        setFieldValue('lastName', data.apellidoPaterno);
        setFieldValue('secondLastName', data.apellidoMaterno);
        const date = functions.standardDateToObject(data.fechaNacimiento);
        if (date) {
            setFieldValue('birthDate', date);
        }
        setFieldValue('validationResult', true);
    }

    async function validateCurp(curp: string) {
        if (curp && curp.length >= 18) {
            setIsLoading(true);
            try {
                const response: EfeResponse<ValidationsCURPResponse> =
                    await ValidationsService.validateCurp({curp: curp, documento: 0});
                if (response && response.data && response.data.curp == curp) {
                    setValues(response.data);
                } else {
                    enqueueSnackbar(Messages.CURP_VALIDATION_ERROR, {
                        variant: SnackbarVariants.ERROR,
                    });
                }
                setIsLoading(false);
            } catch (error) {
                let message = Messages.CURP_VALIDATION_UNAVAILABLE;
                if (error) {
                    const err = error as AxiosError;
                    if (HttpCodes.BAD_REQUEST == err.code || HttpCodes.NOT_FOUND == err.code) {
                        message = Messages.CURP_VALIDATION_ERROR;
                    }
                }
                enqueueSnackbar(message, {
                    variant: SnackbarVariants.ERROR,
                });
                setIsLoading(false);
            }
        } else {
            setFieldValue('validationResult', false);
        }
    }

    async function setCatalogValues(clabe: string) {
        if (fields && fields.length) {
            try {
                setIsLoading(true);
                if (catBanks && catBanks.length) {
                    const bank = catBanks.find(
                        (item: any) => item.id === `40-${clabe.substring(0, 3)}`
                    );
                    if (bank) {
                        setFieldValue('bank', bank.id);
                    }
                }
                setIsLoading(false);
            } catch (error) {
                console.error(error);
            }
        }
    }

    React.useEffect(() => {
        if (!foreign || (foreign && values.foreignCitizen)) {
            validateCurp(values.curp.toUpperCase());
        }
    }, [values.curp]);

    React.useEffect(() => {
        if (values.clabe && values.clabe.length >= 3) {
            setCatalogValues(values.clabe);
        }
    }, [values.clabe]);

    React.useEffect(() => {
        if (values.economicActivity) {
            const fetchData = async () => {
                setIsLoading(true);
                const response = await CatalogServices.getValuesFrom(values.economicActivity);
                if (response && response.data && response.data.values && response.data.values.length) {
                    if (fields && fields.length) {
                        fields[fields.findIndex((field) => field.attrName === 'economicActivityDetail')].catalogValues = [
                            ...response.data.values,
                        ];
                    }
                }
                setIsLoading(false);
            };
            fetchData().catch(console.error).finally(() => {
                setIsLoading(false);
            });
        }
    }, [values.economicActivity]);

    /* eslint-disable react/jsx-handler-names */
    return (
        <Form noValidate>
            {isLoading && useSpinner(isLoading)}
            {fields && fields.length > 0 && (
                <Box m={2}>
                    <EfeSupportMessage />
                    <EfeFormSection
                        fields={fields}
                        values={values}
                        errors={errors}
                        setFieldValue={setFieldValue}
                    />
                    <Grid container direction='row' justifyContent='flex-end'>
                        <Box pt={6} width={160}>
                            <EfeButtonPrimary
                                fullWidth
                                disabled={isSubmitting}
                                type='submit'
                                endIcon={<CallMade />}>
                                {'OK'}
                            </EfeButtonPrimary>
                        </Box>
                    </Grid>
                </Box>
            )}
        </Form>
    );
}

export default InnerFormDocsInfo;
